import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

import { BehaviorSubject, map, of, Subscription, filter } from 'rxjs';


import { AbstractBaseStyleFieldComponent } from '@Common/PageBuilder/components/BaseStyleField/abstract-base-style-field.component';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { AiAnalysisTypes } from '@Common/Mentions/enums/ai-analysis-types.enum';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { coverageInsightTypesOptions } from '@Common/Forms/data/coverage-insight-types-options';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';

import { AnalysisField } from '../../../Shared/models/analysis-field.model';

import { BriefingModel } from '../../models/briefing.model';
import { CoverageInsights } from '../../../Shared/models/coverage-insights.model';
import { AnalysisDisplayElementModel } from '../../models/briefing-analysis-display-element.model';
import { CustomInsights } from '../../../Shared/models/custom-insights.model';
import { GenerateAnalysisDialogComponent } from '../GenerateAnalysisDialog/generate-analysis-dialog.component';
import { BriefingCoverageAnalysisService } from '../../services/briefing-coverage-analysis.service';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import {
    CustomPromptsManagementDialogComponent
} from '../../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';


@Component({
    selector: 'agpr-analysis-field',
    templateUrl: './analysis-field.component.html',
    styleUrls: ['./analysis-field.component.scss'],
    standalone: false
})
export class AnalysisFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnChanges, OnDestroy {
    readonly locatorId: string = 'analysisField';

    readonly buttonTypes = ButtonTypesEnum;
    readonly coverageInsightTypes = AiAnalysisTypes;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly MAX_NAME_LENGTH: number = 64;
    readonly MAX_PROMPT_LENGTH: number = 20000;

    /** Model for managing customizations to mentions */
    @Input() declare field: AnalysisField;
    @Input() block: BlockModel;
    @Input() value: CoverageInsights;

    briefing: BriefingModel;
    briefingDirty: boolean;
    generateOnSend: boolean;

    analysisTypeOptions = coverageInsightTypesOptions;
    selectedAnalysisTypeOption: DropdownOptionModel;

    subscriptions: Subscription[] = [];

    customInsights: CustomInsights[];
    customInsightsOptions: DropdownOptionModel[];
    customInsightOption: DropdownOptionModel;
    customInsightCopy: CustomInsights;
    customInsightValid: boolean = true;
    customInsightSaveTooltip: string;
    openAiPermission: boolean = false;

    /** Custom analysis saved from this panel, waiting to be selected once the refreshed list of insights comes back */
    private createdCustomInsight: { name: string; id: number } = null;

    private animationTrigger = new BehaviorSubject(
        new FullProfileAnimationModel(
            FullProfileDialogAnimationsEnum.slideInOutAnimation,
            OverlayAnimationStateEnum.open
        )
    );

    constructor(
        protected coreDialogService: CoreDialogService,
        public injector: Injector,
        private briefingCoverageAnalysisService: BriefingCoverageAnalysisService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.briefingCoverageAnalysisService.customInsights$.pipe(
                map(x => x.filter(item => item.promptType === CustomInsightType.BRIEFING_ANALYSIS_BLOCK))
            ).subscribe(
                customInsights => {
                    const knownInsightIds = new Set((this.customInsights ?? []).map(insight => insight.id));
                    this.customInsights = customInsights;
                    this.customInsightsOptions = [];
                    this.customInsightsOptions.push(new DropdownOptionModel('Briefings.analysis.customInsight.new', new CustomInsights()));
                    if (this.customInsights) {
                        for (const customInsight of this.customInsights) {
                            this.customInsightsOptions.push(new DropdownOptionModel(customInsight.name, customInsight.clone()));
                        }

                        const createdOption = this.findCreatedCustomInsightOption(knownInsightIds);
                        this.createdCustomInsight = null;
                        if (createdOption) {
                            this.selectCustomPrompt(createdOption);
                        } else if (this.value?.customPromptId) {
                            this.selectCustomInsightOption(
                                this.customInsightsOptions.find(insight => insight.value.id === this.value.customPromptId)
                            );
                        }
                    }
                }
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
        );
        this.analysisTypeOptions = this.field.options ? this.field.options : coverageInsightTypesOptions;
        this.selectedAnalysisTypeOption = (this.value?.aiAnalysisType
            ? this.analysisTypeOptions.find(option => option.value === this.value.aiAnalysisType)
            : null) ?? this.analysisTypeOptions[0];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.value?.currentValue) {
            this.value = changes?.value?.currentValue;
            if (this.value.aiAnalysisType) {
                this.selectedAnalysisTypeOption = this.analysisTypeOptions.find(option => option.value === this.value.aiAnalysisType);
            }
        }

        this.generateOnSend = (this.block.displayElement as AnalysisDisplayElementModel).generateOnSend;
        this.setDefaults();
    }

    get isSurgePeriodSelected(): boolean {
        return (this.block?.displayElement as AnalysisDisplayElementModel)?.useSearchDateFilter === false;
    }

    setupValue() {
        (this.block.displayElement as AnalysisDisplayElementModel).generateOnSend = this.generateOnSend;
        if (this.generateOnSend && !this.value.aiAnalysisType) {
            this.value.aiAnalysisType = AiAnalysisTypes.EXECUTIVE_SUMMARY;
            this.setDefaults();
            this.value.keyTakeawayHeading = 'Key Takeaways';
        }
        this.changes.emit(this.value);
    }

    setDefaults() {
        if (this.value.aiAnalysisType === AiAnalysisTypes.CUSTOM) {
            if (this.value.customPromptId) {
                this.customInsightOption = this.customInsightsOptions?.find(insight => insight.value.id === this.value.customPromptId);
            }
        } else {
            this.value = this.briefingCoverageAnalysisService.setDefaults(this.value);
        }
    }

    analysisTypeChange() {
        this.value.aiAnalysisType = this.selectedAnalysisTypeOption.value;
        if (this.selectedAnalysisTypeOption.value !== AiAnalysisTypes.CUSTOM) {
            this.value.customPromptId = null;
            this.customInsightOption = null;
            this.customInsightCopy = null;
            this.customInsightValid = true;
            this.customInsightSaveTooltip = '';
        }
        this.value.selectedLengthOption = null;
        this.setDefaults();
        this.changes.emit(this.value);
    }


    changedValue(value: CoverageInsights) {
        this.value = value;
        this.changes.emit(this.value);
    }


    selectCustomPrompt(option?: DropdownOptionModel): void {
        if (option?.value?.id) {
            this.value.customPromptId = option?.value?.id;
            this.customInsightOption = option;
            this.customInsightCopy = option?.value?.clone();
            if (this.block?.errors) {
                this.block.errors = this.block.errors.filter(error => !error.includes('Briefings.analysis.errors.customPromptIsRequired'));
            }
        } else {
            this.customInsightOption.value = new CustomInsights();
            this.customInsightCopy = new CustomInsights();
        }
        this.validateCustomInsight();
        this.changes.emit(this.value);
    }

    private selectCustomInsightOption(option?: DropdownOptionModel): void {
        this.customInsightOption = option;
        this.customInsightCopy = option?.value?.clone();
    }

    /**
     * Finds the custom analysis that was just saved from this panel in the refreshed list of insights.
     * The create response does not always carry the new id, in that case the insight is matched by name,
     * only among the ones that were not part of the list before the save.
     */
    private findCreatedCustomInsightOption(knownInsightIds: Set<number>): DropdownOptionModel {
        if (!this.createdCustomInsight) {
            return null;
        }

        if (this.createdCustomInsight.id) {
            return this.customInsightsOptions.find(option => option.value.id === this.createdCustomInsight.id);
        }

        const createdName = this.createdCustomInsight.name?.trim();

        return this.customInsightsOptions.find(option =>
            option.value.name?.trim() === createdName && !knownInsightIds.has(option.value.id)
        );
    }

    async saveCustomInsight() {
        if (this.customInsightOption?.value?.id) {
            this.briefingCoverageAnalysisService
                .updateCustomInsight(this.customInsightOption.value)
                .subscribe({
                    next: (success) => {
                        if (success) {
                            this.customInsightCopy = this.customInsightOption?.value.clone();
                        }
                    }
                });
        } else {
            await this.doCreateCustomInsight();
        }
    }

    async saveAsCustomInsight() {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'nameCustomInsight',
                heading: 'Briefings.analysis.customInsight.header',
                okLabel: 'Briefings.analysis.customAnalysis.save',
                placeholder: 'Briefings.analysis.customAnalysisName.placeholder',
                description: 'Briefings.analysis.customInsight.label',
                initialValue: this.customInsightOption?.value.name,
                submit: (name: string) => {
                    this.customInsightOption.value.name = name;
                    this.doCreateCustomInsight();
                    return of(null);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    doCreateCustomInsight() {
        const newCustomInsight = this.customInsightOption?.value;
        if (!newCustomInsight) {
            return;
        }
        newCustomInsight.promptType = CustomInsightType.BRIEFING_ANALYSIS_BLOCK;
        const createdName = newCustomInsight.name;

        this.briefingCoverageAnalysisService.createCustomInsight(newCustomInsight.clone()).subscribe(id => {
            // The insight is selected when the refreshed list of insights comes back, it is the only place
            // where the option it is bound to exists
            this.createdCustomInsight = { name: createdName, id: id };
            if (id) {
                this.value.customPromptId = id;
                newCustomInsight.id = id;
                this.customInsightCopy = newCustomInsight.clone();
                this.changes.emit(this.value);
            }
        });
    }

    changedCustomValue(value: CustomInsights) {
        this.value.customPromptId = value?.id;
        this.validateCustomInsight();
    }

    validateCustomInsight() {
        if (!this.customInsightOption?.value?.name?.trim()) {
            this.customInsightValid = false;
            this.customInsightSaveTooltip = 'Briefings.analysis.errors.nameRequired';
            return;
        }
        if (!this.customInsightOption?.value?.prompt?.trim()) {
            this.customInsightValid = false;
            this.customInsightSaveTooltip = 'Briefings.analysis.errors.promptRequired';
            return;
        }
        if (this.customInsightOption?.value?.name?.length >= this.MAX_NAME_LENGTH) {
            this.customInsightValid = false;
            this.customInsightSaveTooltip = 'Briefings.analysis.errors.nameMaxLength';
            return;
        }
        if (this.customInsightOption?.value?.prompt?.length >= this.MAX_PROMPT_LENGTH) {
            this.customInsightValid = false;
            this.customInsightSaveTooltip = 'Briefings.analysis.errors.promptMaxLength';
            return;
        }

        this.customInsightValid = true;
        this.customInsightSaveTooltip = '';
    }

    openCustoPromptDialog() {

        const selectedInsight = this.customInsightOption?.value;
        const notSavedInsight = selectedInsight && !selectedInsight.id
            ? selectedInsight.clone()
            : null;

        const savedInsightId = selectedInsight?.id ?? null;

        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: document.getElementById('main-content'),
                selectedPromptType: CustomInsightType.BRIEFING_ANALYSIS_BLOCK
            },
            outputs: {
                dialogClose: () => {
                    if (notSavedInsight) {
                        // Restore unsaved edits
                        this.customInsightOption.value = notSavedInsight;
                    } else if (savedInsightId) {
                        // Re-sync the selected insight after updates or deletion
                        const updatedInsight = this.customInsightsOptions.find((item) => item.value.id === savedInsightId);
                        if (!updatedInsight) {
                            this.customInsightOption = null;
                            this.customInsightCopy = null;
                            this.customInsightValid = false;
                            this.changes.emit(null);
                        } else {
                            this.selectCustomPrompt(updatedInsight);
                        }
                    }
                }
            }
        };
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, io);
    }

    openGenerateAnalysisDialog() {
        const dialogIO = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: document.getElementById('main-content'),
                dataSource: (this.block.displayElement as AnalysisDisplayElementModel).backendDataSource
            },
            outputs: {
                dialogClose: () => {
                }
            }
        };
        this.coreDialogService.open(GenerateAnalysisDialogComponent, dialogIO, this.injector);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }
}
